import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './VenueEditor.css';
import { parseItemsFromDescription, formatDaysLabel, categorizeDeal, categorizeItem, extractPrice, groupDealsByCategory } from '../utils/descriptionParser';
import { ContentItem, ContentItemSection, renderMarkdown } from './ContentItem';
import VenueEditorGuide from './VenueEditorGuide';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function to12Hour(time) {
  if (!time) return '';
  if (/[AP]M/i.test(time)) return time; // already 12-hour
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h)) return time;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m || 0).padStart(2, '0')} ${period}`;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };
const EVENT_TYPES = ['Live Music', 'Trivia', 'Karaoke', 'Comedy', 'Open Mic', 'Sports', 'Special Event', 'Holiday', 'DJ', 'Bingo'];
const EVENT_TYPE_IMAGE = {
  'Live Music':    '/images/event-live-music.png',
  'Trivia':        '/images/event-trivia.png',
  'Karaoke':       '/images/event-karaoke.png',
  'Comedy':        '/images/event-comedy.png',
  'Open Mic':      '/images/event-open-mic.png',
  'Sports':        '/images/event-sports.png',
  'Special Event': '/images/event-special.png',
  'Holiday':       '/images/event-holiday.png',
  'DJ':            '/images/event-dj.png',
  'Bingo':         '/images/event-Bingo.png',
};
const VENUE_TYPES = ['American', 'Asian', 'Bar', 'Brewery', 'Café', 'Indian', 'Italian', 'Mexican', 'Pizza', 'Pub', 'Restaurant', 'Steakhouse', 'Winery'];
const TIERS = ['Basic', 'Silver', 'Platinum'];

function formatDays(days) {
  if (!Array.isArray(days) || !days.length) return '—';
  return days.map(d => DAY_SHORT[d] || d).join(', ');
}


function DayCheckboxes({ value = [], onChange }) {
  const toggle = (day) => {
    onChange(value.includes(day) ? value.filter(d => d !== day) : [...value, day]);
  };
  const allSelected = DAYS.every(d => value.includes(d));
  const toggleAll = () => onChange(allSelected ? [] : [...DAYS]);
  return (
    <div className="ve-day-checkboxes">
      {DAYS.map(day => (
        <label key={day} className={`ve-day-cb ${value.includes(day) ? 'checked' : ''}`}>
          <input type="checkbox" checked={value.includes(day)} onChange={() => toggle(day)} />
          {DAY_SHORT[day]}
        </label>
      ))}
      <label className={`ve-day-cb ve-day-all ${allSelected ? 'checked' : ''}`}>
        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
        All
      </label>
    </div>
  );
}


export default function VenueEditor({ baseId, getAuthHeaders }) {
  // ── List state
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState(new Set(['active']));

  // ── Drawer state
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const cleanseIssueCount = useMemo(() => content ? analyzeContent(content).length : null, [content]);

  // ── Cleanse modal
  const [cleanseModal, setCleanseModal] = useState({ open: false, issues: [], fixing: new Set(), fixed: new Set(), aiFixing: new Set(), aiProposed: {}, aiError: {} });

  // ── Profile form
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // ── Item edit modal
  const [editModal, setEditModal] = useState({ open: false, type: null, item: null, form: {}, saving: false, error: '' });
  const [aiConverting, setAiConverting] = useState(false);
  const [aiConvertError, setAiConvertError] = useState('');
  const noteTextareaRef = useRef(null);

  // ── Content preview modal
  const [previewType, setPreviewType] = useState(null); // 'deals' | 'events' | 'specials'
  const [previewContent, setPreviewContent] = useState(null); // patched content for edit-modal preview

  // ── Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null, parentId: null, label: '' });
  const [deleting, setDeleting] = useState(false);

  // ── Guide
  const [showGuide, setShowGuide] = useState(false);

  // ── New venue modal
  const [showNewVenue, setShowNewVenue] = useState(false);
  const [newVenueForm, setNewVenueForm] = useState({ name: '', type: '', city: '', address: '', description: '', phone: '', website: '' });
  const [newVenueThumbFile, setNewVenueThumbFile] = useState(null);
  const [newVenueThumbPreview, setNewVenueThumbPreview] = useState('');
  const [newVenueSaving, setNewVenueSaving] = useState(false);
  const [newVenueError, setNewVenueError] = useState('');

  // ── Fetch venues
  const fetchVenues = useCallback(async ({ isRetry = false } = {}) => {
    if (!baseId) return;
    if (!isRetry) { setLoading(true); setError(null); }
    try {
      const res = await fetch(`${API_URL}/api/admin/venues-editor?baseId=${baseId}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch venues');
      setVenues(data.venues || []);
      setLoading(false);
    } catch (err) {
      if (!isRetry) {
        // Auto-retry once after 6s — handles Render cold-start timeouts on first open
        setTimeout(() => fetchVenues({ isRetry: true }), 6000);
      } else {
        setError(err.message);
        setLoading(false);
      }
    }
  }, [baseId, getAuthHeaders]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  // ── Fetch content for selected venue
  const fetchContent = useCallback(async (venueId) => {
    setContentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/venues-editor/${venueId}/content?baseId=${baseId}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setContent(data);
      return data;
    } catch (err) {
      setContent({ deals: [], events: [], specials: [] });
      return { deals: [], events: [], specials: [] };
    } finally {
      setContentLoading(false);
    }
  }, [baseId, getAuthHeaders]);

  // ── Open/close drawer
  function openDrawer(venue) {
    setSelectedVenue(venue);
    setActiveTab('profile');
    setProfileForm({
      name: venue.name || '',
      type: venue.type || '',
      city: venue.city || '',
      address: venue.address || '',
      description: venue.description || '',
      phone: venue.phone || '',
      website: venue.website || '',
      facebookUrl: venue.facebookUrl || '',
      instagramUrl: venue.instagramUrl || '',
      tier: venue.tier || 'Basic',
      priority: venue.priority || 0,
      isFeatured: !!venue.isFeatured,
    });
    setProfileError('');
    setProfileSuccess(false);
    setContent(null);
    fetchContent(venue.id);
  }

  function closeDrawer() {
    setSelectedVenue(null);
    setContent(null);
  }

  // ── Save profile
  async function saveProfile() {
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess(false);
    try {
      const res = await fetch(`${API_URL}/api/venues/${selectedVenue.id}/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ baseId, ...profileForm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setVenues(vs => vs.map(v => v.id === selectedVenue.id ? { ...v, ...profileForm } : v));
      setSelectedVenue(v => ({ ...v, ...profileForm }));
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  }

  // ── Thumbnail upload
  async function handleThumbnailUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setThumbnailUploading(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      formData.append('baseId', baseId);
      const headers = getAuthHeaders(null);
      delete headers['Content-Type'];
      const res = await fetch(`${API_URL}/api/venues/${selectedVenue.id}/thumbnail`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      fetchVenues();
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setThumbnailUploading(false);
    }
  }

  // ── Cleanse Data
  function is24Hour(t) { return t && /^\d{1,2}:\d{2}$/.test(t.trim()); }

  function cleanTextValue(text) {
    return text
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u2013\u2014]/g, ' - ')
      .replace(/\(\s*\)/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function checkTextField(text, fixKey) {
    if (!text) return [];
    const flags = [];
    const cleaned = cleanTextValue(text);
    if (cleaned !== text) {
      flags.push({ msg: 'Smart quotes, em-dashes, or empty parentheses detected', fixable: true, fix: { [fixKey]: cleaned }, proposed: cleaned.slice(0, 80) + (cleaned.length > 80 ? '…' : '') });
    }
    const open = (text.match(/\(/g) || []).length;
    const close = (text.match(/\)/g) || []).length;
    if (open !== close) flags.push({ msg: 'Unbalanced parentheses — may display incorrectly', fixable: false, editable: true, aiFixable: fixKey === 'description', aiFixType: 'fix_text' });
    const openB = (text.match(/\[/g) || []).length;
    const closeB = (text.match(/\]/g) || []).length;
    if (openB !== closeB) flags.push({ msg: 'Unbalanced brackets — may display incorrectly', fixable: false, editable: true, aiFixable: fixKey === 'description', aiFixType: 'fix_text' });
    if (/\$(?!\s*\d)/.test(text)) flags.push({ msg: '$ sign with no number — price may not display correctly', fixable: false, editable: true, aiFixable: fixKey === 'description', aiFixType: 'fix_text' });
    if (text.length > 600) flags.push({ msg: `Very long (${text.length} chars) — may be truncated on mobile`, fixable: false, editable: false, aiFixable: fixKey === 'description', aiFixType: 'reformat_description' });
    return flags;
  }

  function analyzeContent(c) {
    const issues = [];
    const push = (obj) => issues.push({ id: Math.random().toString(36).slice(2), ...obj });

    // Generic deal names that add no value as a card header
    const GENERIC_DEAL_NAMES = /^(happy\s*hour|specials?|deal|hh|daily\s*deal|drink\s*specials?|food\s*specials?|bar\s*specials?)$/i;

    (c.deals || []).forEach(deal => {
      const label = deal['Deal Name'] || [formatDays(deal['Days of Week']), to12Hour(deal['Start Time']), to12Hour(deal['End Time'])].filter(Boolean).join(' · ') || 'Unnamed deal';
      if (!deal['Days of Week']?.length) push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'No days of week — deal won\'t show on any day', fixable: false, editable: true });
      if (!deal.Description) push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'No description — card will have no content to display', fixable: false, editable: true });
      if (is24Hour(deal['Start Time'])) push({ contentType: 'deal', recordId: deal.id, label, severity: 'fix', msg: `Start time "${deal['Start Time']}" is 24-hour format`, fix: { startTime: to12Hour(deal['Start Time']) }, proposed: to12Hour(deal['Start Time']), fixable: true });
      if (is24Hour(deal['End Time'])) push({ contentType: 'deal', recordId: deal.id, label, severity: 'fix', msg: `End time "${deal['End Time']}" is 24-hour format`, fix: { endTime: to12Hour(deal['End Time']) }, proposed: to12Hour(deal['End Time']), fixable: true });
      checkTextField(deal.Description, 'description').forEach(f => push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', ...f }));
      // Deal Name quality checks
      if (!deal['Deal Name']) {
        push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'No Deal Name — the card header will be blank. Add the item name (e.g. "$5 Drafts") as the Deal Name for best display.', fixable: false, editable: true, aiFixable: !!deal.Description, aiFixType: 'suggest_name' });
      } else if (GENERIC_DEAL_NAMES.test(deal['Deal Name'].trim())) {
        push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: `Deal Name "${deal['Deal Name']}" is too generic — replace with the specific item or price (e.g. "$6 House Wine") so the card header is meaningful.`, fixable: false, editable: true, aiFixable: !!deal.Description, aiFixType: 'suggest_name' });
      }
      if (deal.Description) {
        const parsed = parseItemsFromDescription(deal.Description);
        if (parsed.length === 0) push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'Description has no parseable items — will display as plain text paragraph. Try a format like "$5 Draft Beer" or "Draft Beer $5".', fixable: false, editable: true, aiFixable: true, aiFixType: 'reformat_description' });
        const parenCount = (deal.Description.match(/[A-Za-z][^,().]*?\s*\(\$\d+(?:\.\d{1,2})?\s*[-–—]/gi) || []).length;
        if (parenCount === 1) push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'Only 1 parenthetical price item — Pattern 0 needs 2+ to activate; price structure won\'t appear', fixable: false, editable: true, aiFixable: true, aiFixType: 'reformat_description' });
        // Flag if description is a near-duplicate of the deal name (redundant data)
        if (deal['Deal Name'] && deal.Description.trim().toLowerCase().replace(/\$[\d.]+/g, '').replace(/\s+/g, ' ') === deal['Deal Name'].trim().toLowerCase().replace(/\$[\d.]+/g, '').replace(/\s+/g, ' ')) {
          push({ contentType: 'deal', recordId: deal.id, label, severity: 'warn', msg: 'Description appears to duplicate the Deal Name — consider using the Description for supporting detail (e.g. "Red or White, Bartender\'s Choice") rather than repeating the item name.', fixable: false, editable: true, aiFixable: true, aiFixType: 'rewrite_as_detail' });
        }
      }
    });

    (c.events || []).forEach(event => {
      const label = event['Event Name'] || 'Unnamed event';
      if (!event['Event Type']) push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: 'No event type set', fixable: false, editable: true });
      if (is24Hour(event['Start Time'])) push({ contentType: 'event', recordId: event.id, label, severity: 'fix', msg: `Start time "${event['Start Time']}" is 24-hour format`, fix: { startTime: to12Hour(event['Start Time']) }, proposed: to12Hour(event['Start Time']), fixable: true });
      if (is24Hour(event['End Time'])) push({ contentType: 'event', recordId: event.id, label, severity: 'fix', msg: `End time "${event['End Time']}" is 24-hour format`, fix: { endTime: to12Hour(event['End Time']) }, proposed: to12Hour(event['End Time']), fixable: true });
      if (event.Recurring && !event['Days of Week']?.length && !event['Recurrence Description']) push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: 'Recurring event with no days or recurrence description', fixable: false, editable: true });
      if (!event.Recurring && !event['Event Start']) push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: 'One-time event with no date set', fixable: false, editable: true });
      if (!event.Recurring && event['Event Start'] && !/^\d{4}-\d{2}-\d{2}(T|$)/.test(event['Event Start'])) push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: `Event date "${event['Event Start']}" format unrecognized — will show as "Date TBD"`, fixable: false, editable: true });
      if (event['Event Type'] && !EVENT_TYPES.includes(event['Event Type'])) push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: `Event type "${event['Event Type']}" is not in the valid list — won't match category images`, fixable: false, editable: true });
      checkTextField(event.Description, 'description').forEach(f => push({ contentType: 'event', recordId: event.id, label, severity: 'warn', ...f }));
      checkTextField(event['Event Name'], 'eventName').forEach(f => push({ contentType: 'event', recordId: event.id, label, severity: 'warn', msg: `Name: ${f.msg}`, ...f }));
    });

    // Generic special names that don't add value as a card header
    const GENERIC_SPECIAL_NAMES = /^(special|daily\s*special|specials?|deal|food\s*special|drink\s*special)$/i;

    (c.specials || []).forEach(special => {
      const label = special['Special Name'] || 'Unnamed special';
      if (!special['Days of Week']?.length) push({ contentType: 'special', recordId: special.id, label, severity: 'warn', msg: 'No days of week set — special won\'t appear on any day', fixable: false, editable: true });
      // Special Name quality checks
      if (!special['Special Name']) {
        push({ contentType: 'special', recordId: special.id, label, severity: 'warn', msg: 'No Special Name — the card header will be blank. Add a name like "Taco Tuesday" or "Fish Fry Friday" for best display.', fixable: false, editable: true, aiFixable: !!special.Description, aiFixType: 'suggest_name' });
      } else if (GENERIC_SPECIAL_NAMES.test(special['Special Name'].trim())) {
        push({ contentType: 'special', recordId: special.id, label, severity: 'warn', msg: `Special Name "${special['Special Name']}" is too generic — use a specific name (e.g. "Wing Wednesday", "Fish Fry Friday") so the card header is meaningful.`, fixable: false, editable: true, aiFixable: !!special.Description, aiFixType: 'suggest_name' });
      }
      if (!special.Description) push({ contentType: 'special', recordId: special.id, label, severity: 'warn', msg: 'No description — add details about what the special includes and any pricing.', fixable: false, editable: true });
      checkTextField(special.Description, 'description').forEach(f => push({ contentType: 'special', recordId: special.id, label, severity: 'warn', ...f }));
      if (special.Description && /\$/.test(special.Description)) {
        const parsed = parseItemsFromDescription(special.Description);
        if (parsed.length === 1) push({ contentType: 'special', recordId: special.id, label, severity: 'warn', msg: 'Only 1 parsed price item — needs 2+ for structured display; will fall back to plain text', fixable: false, editable: true, aiFixable: true, aiFixType: 'reformat_description' });
      }
    });

    return issues;
  }

  function openCleanseModal() {
    const issues = analyzeContent(content || { deals: [], events: [], specials: [] });
    setCleanseModal({ open: true, issues, fixing: new Set(), fixed: new Set(), aiFixing: new Set(), aiProposed: {}, aiError: {} });
  }

  function openEditFromCleanse(contentType, recordId) {
    setCleanseModal(m => ({ ...m, open: false }));
    const map = { deal: content?.deals, event: content?.events, special: content?.specials };
    const record = (map[contentType] || []).find(r => r.id === recordId);
    if (record) openEditModal(contentType, record);
  }

  async function applyFix(issue) {
    const venueId = selectedVenue.id;
    const base = `${API_URL}/api/admin/venues-editor/${venueId}`;
    const paths = { deal: 'deals', event: 'events', special: 'specials' };
    const url = `${base}/${paths[issue.contentType]}/${issue.recordId}`;
    setCleanseModal(m => ({ ...m, fixing: new Set([...m.fixing, issue.id]) }));
    try {
      const res = await fetch(url, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ ...issue.fix, baseId }) });
      if (!res.ok) throw new Error('Fix failed');
      const fresh = await fetchContent(venueId);
      // Re-analyze with fresh content so the issues list stays live after each fix.
      // Old issue IDs become stale — that's fine, resolved issues won't appear in newIssues,
      // and pending aiProposed entries with old IDs simply won't render.
      if (fresh) {
        const newIssues = analyzeContent(fresh);
        setCleanseModal(m => {
          const fixing = new Set(m.fixing); fixing.delete(issue.id);
          return { ...m, fixing, fixed: new Set(), issues: newIssues };
        });
      } else {
        setCleanseModal(m => {
          const fixing = new Set(m.fixing); fixing.delete(issue.id);
          return { ...m, fixing, fixed: new Set([...m.fixed, issue.id]) };
        });
      }
    } catch (err) {
      setCleanseModal(m => { const fixing = new Set(m.fixing); fixing.delete(issue.id); return { ...m, fixing }; });
    }
  }

  async function fixAll() {
    const fixable = cleanseModal.issues.filter(i => i.fixable && !cleanseModal.fixed.has(i.id));
    for (const issue of fixable) await applyFix(issue);
  }

  async function handleAiFix(issue) {
    const map = { deal: content?.deals, event: content?.events, special: content?.specials };
    const record = (map[issue.contentType] || []).find(r => r.id === issue.recordId);
    const description = record?.Description || '';
    const name = issue.contentType === 'deal' ? (record?.['Deal Name'] || '') : (record?.['Special Name'] || '');

    setCleanseModal(m => ({ ...m, aiFixing: new Set([...m.aiFixing, issue.id]), aiError: { ...m.aiError, [issue.id]: '' } }));
    try {
      const res = await fetch(`${API_URL}/api/admin/venues-editor/ai-fix-issue`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ description, name, aiFixType: issue.aiFixType, contentType: issue.contentType, baseId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI fix failed');
      setCleanseModal(m => {
        const aiFixing = new Set(m.aiFixing); aiFixing.delete(issue.id);
        return { ...m, aiFixing, aiProposed: { ...m.aiProposed, [issue.id]: data } };
      });
    } catch (err) {
      setCleanseModal(m => {
        const aiFixing = new Set(m.aiFixing); aiFixing.delete(issue.id);
        return { ...m, aiFixing, aiError: { ...m.aiError, [issue.id]: err.message } };
      });
    }
  }

  async function handleAiApply(issue) {
    const proposed = cleanseModal.aiProposed[issue.id];
    if (!proposed) return;
    const fix = {};
    if (proposed.description !== undefined) fix.description = proposed.description;
    if (proposed.name !== undefined) {
      fix[issue.contentType === 'deal' ? 'dealName' : 'specialName'] = proposed.name;
    }
    setCleanseModal(m => { const ap = { ...m.aiProposed }; delete ap[issue.id]; return { ...m, aiProposed: ap }; });
    await applyFix({ ...issue, fix, fixable: true });
  }

  function handleAiDiscard(issueId) {
    setCleanseModal(m => { const ap = { ...m.aiProposed }; delete ap[issueId]; return { ...m, aiProposed: ap }; });
  }

  async function fixAllWithAI() {
    const pending = cleanseModal.issues.filter(i =>
      i.aiFixable && !cleanseModal.fixed.has(i.id) && !cleanseModal.aiProposed[i.id] && !cleanseModal.aiFixing.has(i.id)
    );
    // Fire all requests concurrently — each one updates state independently as it resolves
    await Promise.allSettled(pending.map(issue => handleAiFix(issue)));
  }

  // ── Auto-split a single item row when user types comma-separated prices into the name field
  // e.g. "Burgers $8, Chicken Sandwich $8" → two rows
  function splitItemIfMulti(items, idx) {
    const row = items[idx];
    const name = (row.name || '').trim();
    // Only trigger when there's a comma AND a price-like token in the name
    if (!name.includes(',') || !/\$\d/.test(name)) return items;
    const parsed = parseItemsFromDescription(name);
    if (parsed.length <= 1) return items;
    const splitRows = parsed.map(p => ({
      name: p.item || '',
      price: p.price || row.price || '',
      description: row.description || '',
      categoryOverride: p.categoryOverride || row.categoryOverride || '',
    }));
    return [...items.slice(0, idx), ...splitRows, ...items.slice(idx + 1)];
  }

  // ── Eagerly split all rows that still have comma+price patterns in the name.
  // Called in saveItem so the split is guaranteed regardless of whether onBlur fired.
  function resolveAllSplits(items) {
    return items.flatMap(row => {
      const name = (row.name || '').trim();
      if (!name.includes(',') || !/\$\d/.test(name)) return [row];
      const parsed = parseItemsFromDescription(name);
      if (parsed.length <= 1) return [row];
      return parsed.map(p => ({
        name: p.item || '',
        price: p.price || row.price || '',
        description: row.description || '',
        categoryOverride: p.categoryOverride || row.categoryOverride || '',
      }));
    });
  }

  // ── Parse description into structured item rows
  function descriptionToItems(description) {
    if (!description) return [];
    const parsed = parseItemsFromDescription(description);
    if (parsed.length === 0) return [];
    return parsed.map(p => ({ name: p.item || '', price: p.price || '', description: p.description || '', categoryOverride: p.categoryOverride || '' }));
  }

  // ── Build a content snapshot patched with the current edit form values
  // Used so Preview from the edit modal shows unsaved changes.
  function buildPreviewContent() {
    const { type, item, form } = editModal;
    const desc = itemsToDescription(resolveAllSplits(form.items || []));
    if (type === 'deal') {
      const patched = {
        id: item?.id || '__preview_new__',
        'Deal Name': form.dealName || '',
        Description: desc,
        'Days of Week': form.daysOfWeek || [],
        'Start Time': form.startTime || '',
        'End Time': form.endTime || '',
        Recurring: form.recurring,
        'Recurrence Description': form.recurrenceDescription || '',
        'Hide Title': form.showTitle === false,
      };
      const deals = item
        ? (content.deals || []).map(d => d.id === item.id ? patched : d)
        : [...(content.deals || []), patched];
      return { ...content, deals };
    }
    if (type === 'special') {
      const patched = {
        id: item?.id || '__preview_new__',
        'Special Name': form.specialName || '',
        Description: desc,
        'Days of Week': form.daysOfWeek || [],
        'Start Time': form.startTime || '',
        'End Time': form.endTime || '',
        Recurring: form.recurring,
        'Recurrence Description': form.recurrenceDescription || '',
        'Hide Title': form.showTitle === false,
      };
      const specials = item
        ? (content.specials || []).map(s => s.id === item.id ? patched : s)
        : [...(content.specials || []), patched];
      return { ...content, specials };
    }
    return content;
  }

  // ── Serialize structured item rows back to canonical Description format
  // Newline format when any item has a description; comma format otherwise.
  function itemsToDescription(items) {
    const filled = items.filter(i => i.name.trim());
    const hasDesc = filled.some(i => (i.description || '').trim());
    const hasNonDollarPrice = filled.some(i => { const p = i.price.trim(); return p && !/^\$\d+(\.\d{1,2})?$/.test(p); });
    const hasCatOverride = filled.some(i => i.categoryOverride);
    if (hasDesc || hasNonDollarPrice || hasCatOverride) {
      return filled
        .flatMap(i => {
          const tag = i.categoryOverride ? ` [${i.categoryOverride.toLowerCase()}]` : '';
          let line;
          if (i.price.trim()) {
            line = `${i.name.trim()} | ${i.price.trim()}${tag}`;
          } else if (tag) {
            line = `${i.name.trim()} | ${tag.trim()}`;
          } else {
            line = i.name.trim();
          }
          const desc = i.description ? i.description.replace(/\n+/g, ' ').trim() : '';
          return desc ? [line, desc] : [line];
        })
        .join('\n');
    }
    return filled
      .map(i => i.price.trim() ? `${i.name.trim()} ${i.price.trim()}` : i.name.trim())
      .join(', ');
  }

  // ── Open item edit/add modal
  function openEditModal(type, item) {
    let form = {};
    if (type === 'deal') {
      const rawDesc = (item?.Description || '').trim();
      const parsedItems = descriptionToItems(rawDesc);
      form = {
        dealName: item?.['Deal Name'] || '',
        description: rawDesc,
        items: parsedItems.length > 0 ? parsedItems : [{ name: '', price: '', description: '', categoryOverride: '' }],
        startTime: to12Hour(item?.['Start Time'] || ''),
        endTime: to12Hour(item?.['End Time'] || ''),
        daysOfWeek: item?.['Days of Week'] || [],
        recurring: item?.Recurring !== undefined ? !!item.Recurring : true,
        recurrenceDescription: item?.['Recurrence Description'] || '',
        showTitle: item?.['Hide Title'] !== true,
      };
    } else if (type === 'event') {
      form = {
        eventName: item?.['Event Name'] || '',
        eventType: item?.['Event Type'] || '',
        recurring: !!item?.Recurring,
        daysOfWeek: item?.['Days of Week'] || [],
        recurrenceDescription: item?.['Recurrence Description'] || '',
        eventStart: item?.['Event Start'] ? item['Event Start'].slice(0, 10) : '',
        eventEnd: item?.['Event End'] ? item['Event End'].slice(0, 10) : '',
        startTime: to12Hour(item?.['Start Time'] || ''),
        endTime: to12Hour(item?.['End Time'] || ''),
        description: item?.Description || '',
        existingImageUrl: item?.['Event_Image']?.[0]?.url || item?.['Image']?.[0]?.url || '',
        imageFile: null,
        imagePreviewUrl: '',
      };
    } else if (type === 'special') {
      const rawDesc = (item?.Description || '').trim();
      const parsedItems = descriptionToItems(rawDesc);
      form = {
        specialName: item?.['Special Name'] || '',
        description: rawDesc,
        items: parsedItems.length > 0 ? parsedItems : [{ name: '', price: '', description: '', categoryOverride: '' }],
        daysOfWeek: item?.['Days of Week'] || [],
        startTime: to12Hour(item?.['Start Time'] || ''),
        endTime: to12Hour(item?.['End Time'] || ''),
        recurring: item?.Recurring !== undefined ? !!item.Recurring : false,
        recurrenceDescription: item?.['Recurrence Description'] || '',
        showTitle: item?.['Hide Title'] !== true,
      };
    }
    setEditModal({ open: true, type, item: item || null, form, saving: false, error: '' });
    setAiConvertError('');
  }

  // ── Save item (create or update)
  async function saveItem() {
    const { type, item, form } = editModal;
    setEditModal(m => ({ ...m, saving: true, error: '' }));
    const isNew = !item;
    const venueId = selectedVenue.id;

    // Serialize structured items back to canonical Description for deals + specials.
    // resolveAllSplits handles the case where the user didn't blur the name field
    // before clicking Save (onBlur state update would be stale).
    let payload = { ...form, baseId };
    if (type === 'deal' || type === 'special') {
      const serialized = itemsToDescription(resolveAllSplits(form.items || []));
      // If all item rows are empty, preserve the original description rather than
      // wiping it — protects legacy free-text descriptions that couldn't be parsed.
      const description = serialized || form.description || '';
      payload = { ...payload, description };
    }

    try {
      const base = `${API_URL}/api/admin/venues-editor/${venueId}`;
      let url, method;
      if (type === 'deal') {
        url = isNew ? `${base}/deals` : `${base}/deals/${item.id}`;
        method = isNew ? 'POST' : 'PUT';
      } else if (type === 'event') {
        url = isNew ? `${base}/events` : `${base}/events/${item.id}`;
        method = isNew ? 'POST' : 'PUT';
      } else {
        url = isNew ? `${base}/specials` : `${base}/specials/${item.id}`;
        method = isNew ? 'POST' : 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');

      // Upload event image if one was selected
      if (type === 'event' && form.imageFile) {
        const savedEventId = data.event?.id || item?.id;
        if (savedEventId) {
          const imgData = new FormData();
          imgData.append('image', form.imageFile);
          imgData.append('baseId', baseId);
          const imgHeaders = getAuthHeaders(null);
          delete imgHeaders['Content-Type'];
          const imgRes = await fetch(`${API_URL}/api/admin/venues-editor/${venueId}/events/${savedEventId}/image`, {
            method: 'POST',
            headers: imgHeaders,
            body: imgData,
          });
          if (!imgRes.ok) {
            const imgErr = await imgRes.json().catch(() => ({}));
            throw new Error(imgErr.error || 'Event saved but image upload failed');
          }
        }
      }

      await fetchContent(venueId);
      fetchVenues();
      setEditModal({ open: false, type: null, item: null, form: {}, saving: false, error: '' });
    } catch (err) {
      setEditModal(m => ({ ...m, saving: false, error: err.message }));
    }
  }

  // ── AI description conversion
  async function convertWithAI() {
    const description = editModal.form.description;
    if (!description?.trim()) return;
    setAiConverting(true);
    setAiConvertError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/venues-editor/convert-description`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ description, contentType: editModal.type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Conversion failed');
      setEditModal(m => ({ ...m, form: { ...m.form, items: data.items } }));
    } catch (err) {
      setAiConvertError(err.message);
    } finally {
      setAiConverting(false);
    }
  }

  // ── Delete
  function confirmDelete(type, id, label, parentId = null) {
    setDeleteConfirm({ open: true, type, id, label, parentId });
  }

  async function executeDelete() {
    const { type, id, parentId } = deleteConfirm;
    setDeleting(true);
    try {
      let url;
      const venueId = parentId || selectedVenue?.id;
      if (type === 'venue') url = `${API_URL}/api/admin/venues-editor/${id}?baseId=${baseId}`;
      else if (type === 'deal') url = `${API_URL}/api/admin/venues-editor/${venueId}/deals/${id}?baseId=${baseId}`;
      else if (type === 'event') url = `${API_URL}/api/admin/venues-editor/${venueId}/events/${id}?baseId=${baseId}`;
      else url = `${API_URL}/api/admin/venues-editor/${venueId}/specials/${id}?baseId=${baseId}`;

      const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Delete failed');

      if (type === 'venue') {
        setVenues(vs => vs.filter(v => v.id !== id));
        if (selectedVenue?.id === id) closeDrawer();
      } else {
        await fetchContent(selectedVenue.id);
        fetchVenues();
      }
      setDeleteConfirm({ open: false, type: null, id: null, parentId: null, label: '' });
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setDeleting(false);
    }
  }

  // ── Create new venue
  async function createVenue(e) {
    e.preventDefault();
    setNewVenueSaving(true);
    setNewVenueError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/venues-editor`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...newVenueForm, baseId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create venue');

      // Upload thumbnail if one was selected
      if (newVenueThumbFile && data.venue?.id) {
        try {
          const formData = new FormData();
          formData.append('thumbnail', newVenueThumbFile);
          formData.append('baseId', baseId);
          const headers = getAuthHeaders(null);
          delete headers['Content-Type'];
          await fetch(`${API_URL}/api/venues/${data.venue.id}/thumbnail`, {
            method: 'POST',
            headers,
            body: formData,
          });
        } catch (upErr) {
          // Non-fatal: venue created, thumbnail upload failed
          console.error('Thumbnail upload failed:', upErr);
        }
      }

      setShowNewVenue(false);
      setNewVenueForm({ name: '', type: '', city: '', address: '', description: '', phone: '', website: '' });
      setNewVenueThumbFile(null);
      setNewVenueThumbPreview('');
      await fetchVenues();

      if (data.venue?.id) {
        openDrawer({
          id: data.venue.id,
          name: data.venue.Name || newVenueForm.name,
          type: data.venue.Type || newVenueForm.type,
          city: data.venue.City || newVenueForm.city,
          address: data.venue.Address || newVenueForm.address,
          description: data.venue.Description || newVenueForm.description,
          phone: data.venue['Phone Number'] || newVenueForm.phone,
          website: data.venue.Website || newVenueForm.website,
          facebookUrl: '', instagramUrl: '',
          thumbnailImage: [],
          tier: 'Basic', priority: 0, isFeatured: false,
          dealsCount: 0, eventsCount: 0, specialsCount: 0,
        });
      }
    } catch (err) {
      setNewVenueError(err.message);
    } finally {
      setNewVenueSaving(false);
    }
  }

  // ── Filter logic
  const FILTERS_STATUS  = [{ key: 'active', label: 'Active' }];
  const FILTERS_CONTENT = [
    { key: 'has_events', label: 'Has Events' },
    { key: 'has_hh',     label: 'Has HH' },
    { key: 'has_ds',     label: 'Has DS' },
  ];

  const isActive = v => v.status === 'Active' || v.status === 'active' || v.status === '';

  function toggleFilter(key) {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const noFilters = activeFilters.size === 0;

  const filteredVenues = venues.filter(v => {
    if (!(!search || v.name.toLowerCase().includes(search.toLowerCase()))) return false;
    if (noFilters) return true;
    if (activeFilters.has('active')     && !isActive(v))        return false;
    if (activeFilters.has('has_events') && v.eventsCount === 0)  return false;
    if (activeFilters.has('has_hh')     && v.dealsCount === 0)   return false;
    if (activeFilters.has('has_ds')     && v.specialsCount === 0) return false;
    return true;
  });

  const filterCounts = {
    active:     venues.filter(isActive).length,
    has_events: venues.filter(v => v.eventsCount > 0).length,
    has_hh:     venues.filter(v => v.dealsCount > 0).length,
    has_ds:     venues.filter(v => v.specialsCount > 0).length,
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="ve-container">

      {/* Top bar */}
      <div className="ve-topbar">
        <input
          className="ve-search"
          placeholder="Search venues..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="ve-help-btn" title="Open Venue Editor guide" onClick={() => setShowGuide(true)}>?</button>
        <button className="ve-new-btn" onClick={() => setShowNewVenue(true)}>+ New Venue</button>
      </div>

      {/* Filter pills */}
      <div className="ve-filters">
        <button
          className={`ve-filter-tab ${noFilters ? 'active' : ''}`}
          onClick={() => setActiveFilters(new Set())}
        >
          All
          <span className="ve-filter-count">{venues.length}</span>
        </button>
        {FILTERS_STATUS.map(f => (
          <button
            key={f.key}
            className={`ve-filter-tab ${activeFilters.has(f.key) ? 'active' : ''}`}
            onClick={() => toggleFilter(f.key)}
          >
            {f.label}
            <span className="ve-filter-count">{filterCounts[f.key]}</span>
          </button>
        ))}
        <div className="ve-filter-divider" />
        {FILTERS_CONTENT.map(f => (
          <button
            key={f.key}
            className={`ve-filter-tab ${activeFilters.has(f.key) ? 'active' : ''}`}
            onClick={() => toggleFilter(f.key)}
          >
            {f.label}
            <span className="ve-filter-count">{filterCounts[f.key]}</span>
          </button>
        ))}
      </div>

      {/* Venue list */}
      {loading && <div className="ve-loading">Loading venues...</div>}
      {error && <div className="ve-error">{error} <button className="ve-retry-btn" onClick={() => fetchVenues()}>Retry</button></div>}
      {!loading && !error && (
        <div className="ve-list">
          {filteredVenues.length === 0 && (
            <div className="ve-empty">No venues match this filter.</div>
          )}
          {filteredVenues.map(venue => (
            <div key={venue.id} className="ve-row">
              <div className="ve-row-thumb">
                {venue.thumbnailImage?.[0]?.url
                  ? <img src={venue.thumbnailImage[0].url} alt={venue.name} />
                  : <div className="ve-row-thumb-placeholder">{venue.name?.[0]?.toUpperCase() || '?'}</div>
                }
              </div>
              <div className="ve-row-info">
                <div className="ve-row-name">{venue.name}</div>
                <div className="ve-row-desc">{venue.description || <em>No description</em>}</div>
              </div>
              <div className="ve-row-indicators">
                <span className={`ve-ind ${venue.dealsCount > 0 ? 've-ind-on' : 've-ind-off'}`}
                  title={`${venue.dealsCount} happy hour deal${venue.dealsCount !== 1 ? 's' : ''}`}>
                  HH {venue.dealsCount > 0 ? venue.dealsCount : '—'}
                </span>
                <span className={`ve-ind ${venue.eventsCount > 0 ? 've-ind-on' : 've-ind-off'}`}
                  title={`${venue.eventsCount} event${venue.eventsCount !== 1 ? 's' : ''}`}>
                  EVT {venue.eventsCount > 0 ? venue.eventsCount : '—'}
                </span>
                <span className={`ve-ind ${venue.specialsCount > 0 ? 've-ind-on' : 've-ind-off'}`}
                  title={`${venue.specialsCount} special${venue.specialsCount !== 1 ? 's' : ''}`}>
                  SPL {venue.specialsCount > 0 ? venue.specialsCount : '—'}
                </span>
              </div>
              <div className="ve-row-actions">
                <button className="ve-edit-btn" onClick={() => openDrawer(venue)}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Drawer */}
      {selectedVenue && (
        <>
          <div className="ve-backdrop" onClick={closeDrawer} />
          <div className="ve-drawer">
            <div className="ve-drawer-header">
              <div className="ve-drawer-title">
                {selectedVenue.thumbnailImage?.[0]?.url && (
                  <img className="ve-drawer-logo" src={selectedVenue.thumbnailImage[0].url} alt="" />
                )}
                <span>{selectedVenue.name}</span>
              </div>
              <div className="ve-drawer-header-actions">
                <button className="ve-drawer-close" onClick={closeDrawer}>✕</button>
              </div>
            </div>

            <div className="ve-drawer-tabs">
              {[
                { key: 'profile', label: 'Profile' },
                { key: 'deals', label: 'Happy Hours', count: content?.deals.length },
                { key: 'events', label: 'Events', count: content?.events.length },
                { key: 'specials', label: 'Daily Specials', count: content?.specials.length },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`ve-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="ve-tab-count">{tab.count}</span>
                  )}
                </button>
              ))}
            </div>

            <div className="ve-drawer-body">

              {/* Profile */}
              {activeTab === 'profile' && <div className="ve-profile-form">
                  <div className="ve-thumb-row">
                    {selectedVenue.thumbnailImage?.[0]?.url
                      ? <img className="ve-thumb-preview" src={selectedVenue.thumbnailImage[0].url} alt="thumbnail" />
                      : <div className="ve-thumb-empty">No image</div>
                    }
                    <label className="ve-thumb-upload-label">
                      {thumbnailUploading ? 'Uploading...' : selectedVenue.thumbnailImage?.[0]?.url ? 'Replace image' : 'Upload image'}
                      <input type="file" accept="image/*" onChange={handleThumbnailUpload} disabled={thumbnailUploading} hidden />
                    </label>
                  </div>

                  <div className="ve-form-grid">
                    <div className="ve-form-field ve-field-full">
                      <label>Venue Name</label>
                      <input value={profileForm.name || ''} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>Type</label>
                      <select value={profileForm.type || ''} onChange={e => setProfileForm(f => ({ ...f, type: e.target.value }))}>
                        <option value="">— select —</option>
                        {VENUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="ve-form-field">
                      <label>City</label>
                      <input value={profileForm.city || ''} onChange={e => setProfileForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div className="ve-form-field ve-field-full">
                      <label>Description</label>
                      <textarea rows={3} value={profileForm.description || ''} onChange={e => setProfileForm(f => ({ ...f, description: e.target.value }))} />
                    </div>
                    <div className="ve-form-field ve-field-full">
                      <label>Address</label>
                      <input value={profileForm.address || ''} onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>Phone</label>
                      <input value={profileForm.phone || ''} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>Website</label>
                      <input value={profileForm.website || ''} onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>Facebook URL</label>
                      <input value={profileForm.facebookUrl || ''} onChange={e => setProfileForm(f => ({ ...f, facebookUrl: e.target.value }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>Instagram URL</label>
                      <input value={profileForm.instagramUrl || ''} onChange={e => setProfileForm(f => ({ ...f, instagramUrl: e.target.value }))} />
                    </div>
                  </div>

                  <div className="ve-admin-fields">
                    <div className="ve-form-field ve-field-checkbox">
                      <label>
                        <input type="checkbox" checked={!!profileForm.isFeatured} onChange={e => setProfileForm(f => ({ ...f, isFeatured: e.target.checked }))} />
                        Featured
                      </label>
                    </div>
                  </div>

                  {profileError && <div className="ve-form-error">{profileError}</div>}
                  {profileSuccess && <div className="ve-form-success">Profile saved.</div>}
                  <div className="ve-form-actions">
                    <button className="ve-save-btn" onClick={saveProfile} disabled={profileSaving}>
                      {profileSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </div>}

              {/* Happy Hours */}
              {activeTab === 'deals' && (contentLoading
                  ? <div className="ve-loading-sm">Loading...</div>
                  : <>
                      <div className="ve-tab-actions">
                        <button className="ve-add-item-btn" title="Add a new happy hour deal for this venue" onClick={() => openEditModal('deal', null)}>+ Add Deal</button>
                        {content && <button className="ve-preview-btn" title="See how happy hour deals will look on the consumer site" onClick={() => setPreviewType('deals')}>Preview</button>}
                      </div>
                      {content?.deals?.length === 0 && <div className="ve-content-empty">No happy hour deals yet.</div>}
                      {content?.deals?.map(deal => (
                        <div key={deal.id} className="ve-item-row">
                          <div className="ve-item-info">
                            {deal['Deal Name'] && <div className="ve-item-name">{deal['Deal Name']}</div>}
                            <div className="ve-item-days">{formatDays(deal['Days of Week'])}</div>
                            {(deal['Start Time'] || deal['End Time']) && (
                              <div className="ve-item-time">{to12Hour(deal['Start Time'])} – {to12Hour(deal['End Time'])}</div>
                            )}
                            <div className="ve-item-desc">{deal.Description || <em>No description</em>}</div>
                          </div>
                          <div className="ve-item-actions">
                            <button className="ve-edit-btn" onClick={() => openEditModal('deal', deal)}>Edit</button>
                            <button className="ve-delete-row-btn" onClick={() => confirmDelete('deal', deal.id, 'this deal', selectedVenue.id)}>🗑</button>
                          </div>
                        </div>
                      ))}
                    </>
                )}

              {/* Events */}
              {activeTab === 'events' && (contentLoading
                  ? <div className="ve-loading-sm">Loading...</div>
                  : <>
                      <div className="ve-tab-actions">
                        <button className="ve-add-item-btn" title="Add a new event for this venue" onClick={() => openEditModal('event', null)}>+ Add Event</button>
                        {content && <button className="ve-preview-btn" title="See how events will look on the consumer site" onClick={() => setPreviewType('events')}>Preview</button>}
                      </div>
                      {content?.events?.length === 0 && <div className="ve-content-empty">No events yet.</div>}
                      {content?.events?.map(event => (
                        <div key={event.id} className="ve-item-row">
                          {(() => {
                            const src = event['Event_Image']?.[0]?.url || event['Image']?.[0]?.url || EVENT_TYPE_IMAGE[event['Event Type']];
                            return src ? <img className="ve-event-list-thumb" src={src} alt="" /> : null;
                          })()}
                          <div className="ve-item-info">
                            <div className="ve-item-name">
                              {event['Event Name']}
                              {event['Event Type'] && <span className="ve-event-type-badge">{event['Event Type']}</span>}
                            </div>
                            <div className="ve-item-days">
                              {event.Recurring
                                ? (event['Recurrence Description'] || formatDays(event['Days of Week']))
                                : (() => { const d = event['Event Start'] ? new Date(event['Event Start'] + 'T12:00:00') : null; return (d && !isNaN(d)) ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'; })()
                              }
                            </div>
                            {(event['Start Time'] || event['End Time']) && (
                              <div className="ve-item-time">{to12Hour(event['Start Time'])} – {to12Hour(event['End Time'])}</div>
                            )}
                          </div>
                          <div className="ve-item-actions">
                            <button className="ve-edit-btn" onClick={() => openEditModal('event', event)}>Edit</button>
                            <button className="ve-delete-row-btn" onClick={() => confirmDelete('event', event.id, event['Event Name'] || 'this event', selectedVenue.id)}>🗑</button>
                          </div>
                        </div>
                      ))}
                    </>
                )}

              {/* Specials */}
              {activeTab === 'specials' && (contentLoading
                  ? <div className="ve-loading-sm">Loading...</div>
                  : <>
                      <div className="ve-tab-actions">
                        <button className="ve-add-item-btn" title="Add a new daily special for this venue" onClick={() => openEditModal('special', null)}>+ Add Special</button>
                        {content && <button className="ve-preview-btn" title="See how daily specials will look on the consumer site" onClick={() => setPreviewType('specials')}>Preview</button>}
                      </div>
                      {content?.specials?.length === 0 && <div className="ve-content-empty">No daily specials yet.</div>}
                      {content?.specials?.map(special => (
                        <div key={special.id} className="ve-item-row">
                          <div className="ve-item-info">
                            <div className="ve-item-name">{special['Special Name'] || <em>Unnamed special</em>}</div>
                            <div className="ve-item-days">{formatDays(special['Days of Week'])}</div>
                            {special.Description && <div className="ve-item-desc">{special.Description}</div>}
                          </div>
                          <div className="ve-item-actions">
                            <button className="ve-edit-btn" onClick={() => openEditModal('special', special)}>Edit</button>
                            <button className="ve-delete-row-btn" onClick={() => confirmDelete('special', special.id, special['Special Name'] || 'this special', selectedVenue.id)}>🗑</button>
                          </div>
                        </div>
                      ))}
                    </>
                )}

            </div>
          </div>
        </>
      )}

      {/* Item edit/add modal */}
      {editModal.open && (
        <div className="ve-modal-backdrop">
          <div className="ve-modal">
            <div className="ve-modal-header">
              <h3>
                {editModal.item ? 'Edit' : 'Add'}{' '}
                {editModal.type === 'deal' ? 'Happy Hour Deal' : editModal.type === 'event' ? 'Event' : 'Daily Special'}
              </h3>
              <button className="ve-drawer-close" onClick={() => setEditModal(m => ({ ...m, open: false }))}>✕</button>
            </div>
            <div className="ve-modal-body">

              {editModal.type === 'deal' && <>
                <div className="ve-form-field ve-field-full">
                  <div className="ve-label-row">
                    <label>Deal Name</label>
                    <label className="ve-show-title-toggle">
                      <input type="checkbox" checked={editModal.form.showTitle !== false}
                        onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, showTitle: e.target.checked } }))} />
                      Display title
                    </label>
                  </div>
                  <input placeholder="e.g. Happy Hour, Taco Tuesday Wings" value={editModal.form.dealName}
                    onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, dealName: e.target.value } }))} />
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Days of Week</label>
                  <DayCheckboxes
                    value={editModal.form.daysOfWeek}
                    onChange={v => setEditModal(m => ({ ...m, form: { ...m.form, daysOfWeek: v } }))}
                  />
                </div>
                <div className="ve-form-row-2">
                  <div className="ve-form-field">
                    <label>Start Time</label>
                    <input placeholder="e.g. 3:00 PM" value={editModal.form.startTime}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, startTime: e.target.value } }))} />
                  </div>
                  <div className="ve-form-field">
                    <label>End Time</label>
                    <input placeholder="e.g. 6:00 PM" value={editModal.form.endTime}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, endTime: e.target.value } }))} />
                  </div>
                </div>
                <div className="ve-form-field ve-field-checkbox">
                  <label>
                    <input type="checkbox" checked={!!editModal.form.recurring}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurring: e.target.checked } }))} />
                    Recurring (repeats on a schedule)
                  </label>
                </div>
                {editModal.form.recurring && (
                  <div className="ve-form-field ve-field-full">
                    <label>Recurrence Description <span className="ve-field-hint">Optional — for non-standard schedules only (e.g. "1st Monday of the month")</span></label>
                    <input placeholder="e.g. 1st and 3rd Thursday of every month" value={editModal.form.recurrenceDescription}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurrenceDescription: e.target.value } }))} />
                  </div>
                )}
                <div className="ve-form-field ve-field-full">
                  <div className="ve-items-header">
                    <label>Items</label>
                    <button
                      type="button"
                      className="ve-reparse-btn"
                      title="Re-parse the saved description text and reset item rows"
                      onClick={() => {
                        const raw = editModal.form.description || '';
                        const reparsed = descriptionToItems(raw);
                        if (reparsed.length > 0) {
                          setEditModal(m => ({ ...m, form: { ...m.form, items: reparsed } }));
                        } else {
                          const priceMatch = raw.match(/\$[\d.,]+/);
                          setEditModal(m => ({ ...m, form: { ...m.form, items: [{ name: '', price: priceMatch ? priceMatch[0] : '', description: '' }] } }));
                        }
                      }}
                    >↺ Reset from saved text</button>
                  </div>
                  {editModal.form.description && (editModal.form.items || []).every(r => !r.name && !r.price) && (
                    <div className="ve-raw-desc">
                      <div className="ve-raw-desc-header">
                        <span className="ve-raw-desc-icon">⚠️</span>
                        <span className="ve-raw-desc-label">This description couldn't be parsed into items.</span>
                        <button
                          type="button"
                          className={`ve-ai-convert-btn ${aiConverting ? 'loading' : ''}`}
                          disabled={aiConverting}
                          onClick={convertWithAI}
                        >{aiConverting ? '✦ Converting…' : '✦ Convert with AI'} <span className="ve-wip-badge">WIP</span></button>
                      </div>
                      <div className="ve-raw-desc-text">{editModal.form.description}</div>
                      <div className="ve-raw-desc-hint">Fill in the item rows above to replace it, or leave them blank to keep this text as-is.</div>
                      {aiConvertError && <div className="ve-ai-convert-error">{aiConvertError}</div>}
                    </div>
                  )}
                  <div className="ve-item-rows">
                    {(editModal.form.items || []).map((row, idx) => (
                      <div key={idx} className="ve-item-entry">
                        <div className="ve-item-row">
                          <input
                            className="ve-item-name-input"
                            placeholder="Item name"
                            value={row.name}
                            onChange={e => {
                              const items = [...(editModal.form.items || [])];
                              items[idx] = { ...items[idx], name: e.target.value };
                              setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                            }}
                            onBlur={() => {
                              const split = splitItemIfMulti(editModal.form.items || [], idx);
                              if (split !== (editModal.form.items || [])) {
                                setEditModal(m => ({ ...m, form: { ...m.form, items: split } }));
                              }
                            }}
                          />
                          <input
                            className="ve-item-price-input"
                            placeholder="Price (e.g. $5)"
                            value={row.price}
                            onChange={e => {
                              const items = [...(editModal.form.items || [])];
                              items[idx] = { ...items[idx], price: e.target.value };
                              setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                            }}
                          />
                          <div className="ve-item-cat-toggle">
                            {[['D','Drinks'],['F','Food'],['O','Other']].map(([label, full]) => (
                              <button
                                key={label}
                                type="button"
                                className={`ve-item-cat-btn ${row.categoryOverride === full ? 'active' : ''}`}
                                data-tooltip={full}
                                onClick={() => {
                                  const items = [...(editModal.form.items || [])];
                                  items[idx] = { ...items[idx], categoryOverride: row.categoryOverride === full ? '' : full };
                                  setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                                }}
                              >{label}</button>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="ve-item-remove-btn"
                            onClick={() => {
                              const items = (editModal.form.items || []).filter((_, i) => i !== idx);
                              setEditModal(m => ({ ...m, form: { ...m.form, items: items.length ? items : [{ name: '', price: '', description: '', categoryOverride: '' }] } }));
                            }}
                            title="Remove item"
                          >×</button>
                        </div>
                        <input
                          className="ve-item-desc-input"
                          placeholder="Optional description (e.g. excludes seasonal taps)"
                          value={row.description || ''}
                          onChange={e => {
                            const val = e.target.value;
                            const items = [...(editModal.form.items || [])];
                            items[idx] = { ...items[idx], description: val };
                            setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="ve-add-item-btn"
                    onClick={() => setEditModal(m => ({ ...m, form: { ...m.form, items: [...(m.form.items || []), { name: '', price: '', description: '', categoryOverride: '' }] } }))}
                  >+ Add Item</button>
                </div>
              </>}

              {editModal.type === 'event' && <>
                <div className="ve-form-field ve-field-full">
                  <label>Event Name *</label>
                  <input value={editModal.form.eventName}
                    onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, eventName: e.target.value } }))} />
                </div>
                <div className="ve-form-row-2">
                  <div className="ve-form-field">
                    <label>Event Type</label>
                    <select value={editModal.form.eventType}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, eventType: e.target.value } }))}>
                      <option value="">— select —</option>
                      {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="ve-form-field ve-field-checkbox">
                    <label>
                      <input type="checkbox" checked={editModal.form.recurring}
                        onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurring: e.target.checked } }))} />
                      Recurring event
                    </label>
                  </div>
                </div>
                {editModal.form.recurring ? <>
                  <div className="ve-form-field ve-field-full">
                    <label>Days of Week</label>
                    <DayCheckboxes
                      value={editModal.form.daysOfWeek}
                      onChange={v => setEditModal(m => ({ ...m, form: { ...m.form, daysOfWeek: v } }))}
                    />
                  </div>
                  <div className="ve-form-field ve-field-full">
                    <label>Recurrence Description</label>
                    <input placeholder="e.g. 1st and 3rd Thursday of every month" value={editModal.form.recurrenceDescription}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurrenceDescription: e.target.value } }))} />
                  </div>
                </> : (
                  <div className="ve-form-row-2">
                    <div className="ve-form-field">
                      <label>Start Date</label>
                      <input type="date" value={editModal.form.eventStart}
                        onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, eventStart: e.target.value } }))} />
                    </div>
                    <div className="ve-form-field">
                      <label>End Date <span className="ve-field-hint">Optional — multi-day only</span></label>
                      <input type="date" value={editModal.form.eventEnd}
                        onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, eventEnd: e.target.value } }))} />
                    </div>
                  </div>
                )}
                <div className="ve-form-row-2">
                  <div className="ve-form-field">
                    <label>Start Time</label>
                    <input placeholder="e.g. 7:00 PM" value={editModal.form.startTime}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, startTime: e.target.value } }))} />
                  </div>
                  <div className="ve-form-field">
                    <label>End Time</label>
                    <input placeholder="e.g. 10:00 PM" value={editModal.form.endTime}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, endTime: e.target.value } }))} />
                  </div>
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Description</label>
                  <textarea rows={3} value={editModal.form.description}
                    onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, description: e.target.value } }))} />
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Event Image</label>
                  <div className="ve-event-image-row">
                    <div className="ve-event-image-thumb">
                      {(() => {
                        const preview = editModal.form.imagePreviewUrl;
                        const existing = editModal.form.existingImageUrl;
                        const defaultImg = EVENT_TYPE_IMAGE[editModal.form.eventType];
                        const src = preview || existing || defaultImg;
                        return src
                          ? <img src={src} alt="event" />
                          : <div className="ve-event-image-placeholder">No image</div>;
                      })()}
                    </div>
                    <label className="ve-upload-btn">
                      {(editModal.form.existingImageUrl || editModal.form.imagePreviewUrl || EVENT_TYPE_IMAGE[editModal.form.eventType]) ? 'Change image' : 'Add image'}
                      <input type="file" accept="image/*" hidden onChange={e => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const previewUrl = URL.createObjectURL(file);
                        setEditModal(m => ({ ...m, form: { ...m.form, imageFile: file, imagePreviewUrl: previewUrl } }));
                      }} />
                    </label>
                    {editModal.form.imagePreviewUrl && (
                      <span className="ve-image-pending-note">Uploading on save</span>
                    )}
                  </div>
                </div>
              </>}

              {editModal.type === 'special' && <>
                <div className="ve-form-field ve-field-full">
                  <div className="ve-label-row">
                    <label>Special Name</label>
                    <label className="ve-show-title-toggle">
                      <input type="checkbox" checked={editModal.form.showTitle !== false}
                        onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, showTitle: e.target.checked } }))} />
                      Display title
                    </label>
                  </div>
                  <input placeholder="e.g. Taco Tuesday, Fish Fry Friday, Wing Wednesday" value={editModal.form.specialName}
                    onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, specialName: e.target.value } }))} />
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Days of Week</label>
                  <DayCheckboxes
                    value={editModal.form.daysOfWeek}
                    onChange={v => setEditModal(m => ({ ...m, form: { ...m.form, daysOfWeek: v } }))}
                  />
                </div>
                <div className="ve-form-field ve-field-checkbox">
                  <label>
                    <input type="checkbox" checked={!!editModal.form.recurring}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurring: e.target.checked } }))} />
                    Recurring (repeats on a schedule)
                  </label>
                </div>
                {editModal.form.recurring && (
                  <div className="ve-form-field ve-field-full">
                    <label>Recurrence Description <span className="ve-field-hint">Optional — for non-standard schedules only</span></label>
                    <input placeholder="e.g. Every other Friday, 1st and 3rd Tuesday" value={editModal.form.recurrenceDescription}
                      onChange={e => setEditModal(m => ({ ...m, form: { ...m.form, recurrenceDescription: e.target.value } }))} />
                  </div>
                )}
                <div className="ve-form-field ve-field-full">
                  <div className="ve-items-header">
                    <label>Items</label>
                    <button
                      type="button"
                      className="ve-reparse-btn"
                      title="Re-parse the saved description text and reset item rows"
                      onClick={() => {
                        const raw = editModal.form.description || '';
                        const reparsed = descriptionToItems(raw);
                        if (reparsed.length > 0) {
                          setEditModal(m => ({ ...m, form: { ...m.form, items: reparsed } }));
                        } else {
                          const priceMatch = raw.match(/\$[\d.,]+/);
                          setEditModal(m => ({ ...m, form: { ...m.form, items: [{ name: '', price: priceMatch ? priceMatch[0] : '', description: '' }] } }));
                        }
                      }}
                    >↺ Reset from saved text</button>
                  </div>
                  {editModal.form.description && (editModal.form.items || []).every(r => !r.name && !r.price) && (
                    <div className="ve-raw-desc">
                      <div className="ve-raw-desc-header">
                        <span className="ve-raw-desc-icon">⚠️</span>
                        <span className="ve-raw-desc-label">This description couldn't be parsed into items.</span>
                        <button
                          type="button"
                          className={`ve-ai-convert-btn ${aiConverting ? 'loading' : ''}`}
                          disabled={aiConverting}
                          onClick={convertWithAI}
                        >{aiConverting ? '✦ Converting…' : '✦ Convert with AI'} <span className="ve-wip-badge">WIP</span></button>
                      </div>
                      <div className="ve-raw-desc-text">{editModal.form.description}</div>
                      <div className="ve-raw-desc-hint">Fill in the item rows above to replace it, or leave them blank to keep this text as-is.</div>
                      {aiConvertError && <div className="ve-ai-convert-error">{aiConvertError}</div>}
                    </div>
                  )}
                  <div className="ve-item-rows">
                    {(editModal.form.items || []).map((row, idx) => (
                      <div key={idx} className="ve-item-entry">
                        <div className="ve-item-row">
                          <input
                            className="ve-item-name-input"
                            placeholder="Item name"
                            value={row.name}
                            onChange={e => {
                              const items = [...(editModal.form.items || [])];
                              items[idx] = { ...items[idx], name: e.target.value };
                              setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                            }}
                            onBlur={() => {
                              const split = splitItemIfMulti(editModal.form.items || [], idx);
                              if (split !== (editModal.form.items || [])) {
                                setEditModal(m => ({ ...m, form: { ...m.form, items: split } }));
                              }
                            }}
                          />
                          <input
                            className="ve-item-price-input"
                            placeholder="Price (e.g. $5)"
                            value={row.price}
                            onChange={e => {
                              const items = [...(editModal.form.items || [])];
                              items[idx] = { ...items[idx], price: e.target.value };
                              setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                            }}
                          />
                          <div className="ve-item-cat-toggle">
                            {[['D','Drinks'],['F','Food'],['O','Other']].map(([label, full]) => (
                              <button
                                key={label}
                                type="button"
                                className={`ve-item-cat-btn ${row.categoryOverride === full ? 'active' : ''}`}
                                data-tooltip={full}
                                onClick={() => {
                                  const items = [...(editModal.form.items || [])];
                                  items[idx] = { ...items[idx], categoryOverride: row.categoryOverride === full ? '' : full };
                                  setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                                }}
                              >{label}</button>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="ve-item-remove-btn"
                            onClick={() => {
                              const items = (editModal.form.items || []).filter((_, i) => i !== idx);
                              setEditModal(m => ({ ...m, form: { ...m.form, items: items.length ? items : [{ name: '', price: '', description: '', categoryOverride: '' }] } }));
                            }}
                            title="Remove item"
                          >×</button>
                        </div>
                        <input
                          className="ve-item-desc-input"
                          placeholder="Optional description (e.g. no coupons, excludes pizza-of-the-month)"
                          value={row.description || ''}
                          onChange={e => {
                            const val = e.target.value;
                            const items = [...(editModal.form.items || [])];
                            items[idx] = { ...items[idx], description: val };
                            setEditModal(m => ({ ...m, form: { ...m.form, items } }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="ve-add-item-btn"
                    onClick={() => setEditModal(m => ({ ...m, form: { ...m.form, items: [...(m.form.items || []), { name: '', price: '', description: '', categoryOverride: '' }] } }))}
                  >+ Add Item</button>
                </div>
              </>}

              {editModal.error && <div className="ve-form-error">{editModal.error}</div>}
            </div>
            <div className="ve-modal-footer">
              <div className="ve-modal-footer-left">
                {editModal.type !== 'event' && <button type="button" className="ve-footer-action-btn" onClick={() => { setPreviewContent(buildPreviewContent()); setPreviewType(editModal.type === 'deal' ? 'deals' : 'specials'); }}>Preview</button>}
              </div>
              <div className="ve-modal-footer-right">
                <button className="ve-cancel-btn" onClick={() => setEditModal(m => ({ ...m, open: false }))}>Cancel</button>
                <button className="ve-save-btn" onClick={saveItem} disabled={editModal.saving}>
                  {editModal.saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content preview modal — rendered after edit modal so it stacks on top */}
      {previewType && content && (
        <ContentPreview
          type={previewType}
          content={previewContent || content}
          venue={selectedVenue}
          onClose={() => { setPreviewType(null); setPreviewContent(null); }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteConfirm.open && (
        <div className="ve-modal-backdrop">
          <div className="ve-modal ve-modal-sm">
            <div className="ve-modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="ve-modal-body">
              <p>Delete <strong>{deleteConfirm.label}</strong>? This cannot be undone.</p>
            </div>
            <div className="ve-modal-footer">
              <button className="ve-cancel-btn" onClick={() => setDeleteConfirm({ open: false })} disabled={deleting}>Cancel</button>
              <button className="ve-delete-confirm-btn" onClick={executeDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Venue modal */}
      {showNewVenue && (
        <div className="ve-modal-backdrop">
          <div className="ve-modal">
            <div className="ve-modal-header">
              <h3>New Venue</h3>
              <button className="ve-drawer-close" onClick={() => { setShowNewVenue(false); setNewVenueThumbFile(null); setNewVenueThumbPreview(''); }}>✕</button>
            </div>
            <form onSubmit={createVenue}>
              <div className="ve-modal-body">
                <div className="ve-form-field ve-field-full">
                  <label>Venue Image</label>
                  <div className="ve-new-thumb-row">
                    {newVenueThumbPreview ? (
                      <img src={newVenueThumbPreview} alt="preview" className="ve-new-thumb-preview" />
                    ) : (
                      <div className="ve-new-thumb-placeholder">No image</div>
                    )}
                    <div className="ve-new-thumb-controls">
                      <label className="ve-upload-btn">
                        {newVenueThumbFile ? 'Change Image' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setNewVenueThumbFile(file);
                            const reader = new FileReader();
                            reader.onload = ev => setNewVenueThumbPreview(ev.target.result);
                            reader.readAsDataURL(file);
                          }}
                        />
                      </label>
                      {newVenueThumbFile && (
                        <button type="button" className="ve-cancel-btn" onClick={() => { setNewVenueThumbFile(null); setNewVenueThumbPreview(''); }}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Venue Name *</label>
                  <input required value={newVenueForm.name} onChange={e => setNewVenueForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="ve-form-row-2">
                  <div className="ve-form-field">
                    <label>Type *</label>
                    <select required value={newVenueForm.type} onChange={e => setNewVenueForm(f => ({ ...f, type: e.target.value }))}>
                      <option value="">— select —</option>
                      {VENUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="ve-form-field">
                    <label>City *</label>
                    <input required value={newVenueForm.city} onChange={e => setNewVenueForm(f => ({ ...f, city: e.target.value }))} />
                  </div>
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Address</label>
                  <input value={newVenueForm.address} onChange={e => setNewVenueForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="ve-form-field ve-field-full">
                  <label>Description</label>
                  <textarea rows={3} value={newVenueForm.description} onChange={e => setNewVenueForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="ve-form-row-2">
                  <div className="ve-form-field">
                    <label>Phone</label>
                    <input value={newVenueForm.phone} onChange={e => setNewVenueForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div className="ve-form-field">
                    <label>Website</label>
                    <input value={newVenueForm.website} onChange={e => setNewVenueForm(f => ({ ...f, website: e.target.value }))} />
                  </div>
                </div>
                {newVenueError && <div className="ve-form-error">{newVenueError}</div>}
              </div>
              <div className="ve-modal-footer">
                <button type="button" className="ve-cancel-btn" onClick={() => { setShowNewVenue(false); setNewVenueThumbFile(null); setNewVenueThumbPreview(''); }}>Cancel</button>
                <button type="submit" className="ve-save-btn" disabled={newVenueSaving}>
                  {newVenueSaving ? 'Creating...' : 'Create Venue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cleanse Data modal */}
      {cleanseModal.open && (
        <CleanseModal
          modal={cleanseModal}
          venue={selectedVenue}
          content={content}
          onClose={() => setCleanseModal(m => ({ ...m, open: false }))}
          onFix={applyFix}
          onFixAll={fixAll}
          onAiFix={handleAiFix}
          onAiApply={handleAiApply}
          onAiDiscard={handleAiDiscard}
          onAiFixAll={fixAllWithAI}
          onEdit={openEditFromCleanse}
        />
      )}

      {/* Guide modal */}
      {showGuide && <VenueEditorGuide onClose={() => setShowGuide(false)} />}

    </div>
  );
}

// ── DescriptionLivePreview ───────────────────────────────────────────────────

function useDebounced(value, delay) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function DescriptionLivePreview({ name, items: rawItems = [] }) {
  const debouncedItems = useDebounced(rawItems, 300);
  const items = debouncedItems
    .filter(i => (i.name || '').trim())
    .map(i => ({
      item: i.name.trim(),
      price: i.price || '',
      description: i.description || '',
      category: categorizeItem(i.name + ' ' + (i.price || '')),
    }));
  if (items.length === 0) return null;
  const descForCat = items.map(i => i.item + (i.price ? ' ' + i.price : '')).join(', ');
  const dealCat = categorizeDeal(name || '', descForCat);

  // For Food & Drinks deals with items in both categories, show inline DRINKS/FOOD headers
  if (dealCat === 'Food & Drinks' && items.length > 0) {
    const drinkItems = items.filter(i => i.category === 'Drinks');
    const foodItems  = items.filter(i => i.category !== 'Drinks');
    if (drinkItems.length > 0 && foodItems.length > 0) {
      return (
        <div className="ve-desc-preview">
          <div className="ve-desc-preview-label">⚡ Live Preview</div>
          <div className="ve-desc-preview-content">
            <div className="deal-category-section">
              <div className="deal-category-header">
                <span className="category-icon">🍺</span>
                <span className="category-label">Drinks</span>
              </div>
              <div className="deal-category-items">
                {drinkItems.map((item, ii) => (
                  <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === drinkItems.length - 1} />
                ))}
              </div>
            </div>
            <div className="deal-category-section">
              <div className="deal-category-header">
                <span className="category-icon">🍽️</span>
                <span className="category-label">Food</span>
              </div>
              <div className="deal-category-items">
                {foodItems.map((item, ii) => (
                  <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === foodItems.length - 1} />
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Single category — flat list
  return (
    <div className="ve-desc-preview">
      <div className="ve-desc-preview-label">⚡ Live Preview</div>
      <div className="ve-desc-preview-content">
        {items.length >= 2 ? (
          items.map((item, ii) => (
            <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === items.length - 1} />
          ))
        ) : (
          <ContentItem name={name || items[0].item} price={items[0].price} description={items[0].description || null} />
        )}
      </div>
    </div>
  );
}

// ── ContentPreview ────────────────────────────────────────────────────────────

function ContentPreview({ type, content, venue, onClose }) {
  const title = type === 'deals' ? 'Happy Hours' : type === 'events' ? 'Events' : 'Daily Specials';
  return (
    <div className="ve-modal-backdrop" onClick={onClose}>
      <div className="ve-preview-modal" onClick={e => e.stopPropagation()}>
        <div className="ve-preview-header">
          <div>
            <div className="ve-preview-label">Preview · {venue?.name}</div>
            <div className="ve-preview-title">{title}</div>
          </div>
          <button className="ve-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ve-preview-body">
          {type === 'deals'    && <DealsPreview deals={content.deals} />}
          {type === 'events'   && <EventsPreview events={content.events} />}
          {type === 'specials' && <SpecialsPreview specials={content.specials} />}
        </div>
      </div>
    </div>
  );
}

function DealsPreview({ deals }) {
  // Group by schedule, then sort by first day of week
  const scheduleMap = {};
  deals.forEach(deal => {
    const days = deal['Days of Week'] || [];
    const key = `${[...days].sort().join(',')}_${deal['Start Time'] || ''}_${deal['End Time'] || ''}`;
    if (!scheduleMap[key]) {
      const sortIndex = days.length > 0 ? Math.min(...days.map(d => DAY_ORDER.indexOf(d))) : 99;
      scheduleMap[key] = { key, days, startTime: deal['Start Time'] || '', endTime: deal['End Time'] || '', deals: [], sortIndex };
    }
    scheduleMap[key].deals.push(deal);
  });
  const scheduleGroups = Object.values(scheduleMap).sort((a, b) => a.sortIndex - b.sortIndex);

  const renderCategoryDeals = (catDeals, label, icon, hasMultipleCats) => {
    if (catDeals.length === 0) return null;
    return (
      <div key={label} className="deal-category-section">
        {hasMultipleCats && (
          <div className="deal-category-header">
            <span className="category-icon">{icon}</span>
            <span className="category-label">{label}</span>
          </div>
        )}
        <div className="deal-category-items">
          {catDeals.map((deal, di) => {
            // groupDealsByCategory returns deals with .fields and .parsedItems already split by category
            const d = deal.fields;
            const parsedItems = deal.parsedItems || [];
            const desc = d.Description || '';
            const dealName = d['Deal Name'] || '';
            const isRedundantName = /happy\s*hour/i.test(dealName);
            const showTitle = d['Hide Title'] !== true;
            if (parsedItems.length > 0) {
              const showDealName = showTitle && !deal.originalCategory && !isRedundantName && dealName && (parsedItems.length > 1 || showTitle);
              return (
                <div key={di} className="deal deal-with-items">
                  {showDealName && <strong className="deal-name">{dealName}</strong>}
                  {parsedItems.map((item, ii) => (
                    <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === parsedItems.length - 1} />
                  ))}
                </div>
              );
            }
            return (
              <div key={di} className="deal deal-with-items">
                <ContentItem name={dealName || desc} price={extractPrice(desc)} description={dealName ? desc : null} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="vep-section">
      {scheduleGroups.length === 0 && <div className="vep-empty-cat">No deals.</div>}
      {scheduleGroups.map((group, gi) => {
        // Wrap flat deal objects into {id, fields} format expected by groupDealsByCategory
        const wrapped = group.deals.map(d => ({ id: d.id, fields: d }));
        const grouped = groupDealsByCategory(wrapped);
        const hasMultipleCats = [grouped.drinks.length > 0, grouped.food.length > 0, grouped.other.length > 0].filter(Boolean).length > 1;
        return (
          <div key={gi} className="vep-schedule-block">
            <div className="vep-schedule-header">
              <span>🕐 {to12Hour(group.startTime)}{group.endTime ? ` – ${to12Hour(group.endTime)}` : ''}</span>
              <span>📅 {group.days.length ? formatDaysLabel(group.days) || group.days.map(d => d.slice(0, 3)).join(', ') : '—'}</span>
            </div>
            {renderCategoryDeals(grouped.drinks, 'Drinks', '🍺', hasMultipleCats)}
            {renderCategoryDeals(grouped.food,   'Food',   '🍽️', hasMultipleCats)}
            {renderCategoryDeals(grouped.other,  'Specials','⭐', hasMultipleCats)}
          </div>
        );
      })}
    </div>
  );
}

function formatEventDatePreview(event) {
  if (event.Recurring) {
    const startTime = event['Start Time'];
    const timeStr = startTime ? to12Hour(startTime) : null;
    const recDesc = event['Recurrence Description'];
    if (recDesc) return timeStr ? `${recDesc} @ ${timeStr}` : recDesc;
    const days = event['Days of Week'] || [];
    if (days.length) {
      const pluralDays = days.join(', ') + 's';
      return timeStr ? `${pluralDays} @ ${timeStr}` : pluralDays;
    }
    return 'Recurring';
  }
  const ds = event['Event Start'];
  if (!ds) return '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(ds);
  const d = new Date(isDateOnly ? ds + 'T12:00:00' : ds);
  if (isNaN(d)) return '';
  const startTime = event['Start Time'] ? to12Hour(event['Start Time']) : null;
  const dateLabel = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  return startTime ? `${dateLabel} at ${startTime}` : dateLabel;
}

function EventsPreview({ events }) {
  return (
    <div className="vep-section">
      {events.map((event, i) => {
        const imgSrc = event['Event_Image']?.[0]?.url || event['Image']?.[0]?.url || EVENT_TYPE_IMAGE[event['Event Type']];
        const dateStr = formatEventDatePreview(event);
        return (
          <div key={i} className="vep-event">
            {imgSrc && <img className="vep-event-img" src={imgSrc} alt="" />}
            <div className="vep-event-body">
              <div className="vep-event-header">
                <strong>{event['Event Name']}</strong>
              </div>
              {event.Description && <p className="vep-event-desc">{renderMarkdown(event.Description)}</p>}
              <div className="vep-event-details">
                {event['Event Type'] && <span className="vep-event-type">{event['Event Type']}</span>}
                {dateStr && <span className="vep-event-date">📅 {dateStr}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LABEL_ORDER = { 'Daily': -3, 'Weekdays': -2, 'Weekends': -1 };

function SpecialsPreview({ specials }) {
  const groupMap = {};
  specials.forEach(special => {
    const days = special['Days of Week'] || [];
    const recDesc = special['Recurrence Description'] || '';
    const key = recDesc || ([...days].sort().join(',') || 'no-days');
    const label = (recDesc || formatDaysLabel(days) || '—').replace(/\b\w/g, c => c.toUpperCase());
    if (!groupMap[key]) {
      const sortIndex = days.length > 0 ? Math.min(...days.map(d => DAY_ORDER.indexOf(d))) : 99;
      groupMap[key] = { label, specials: [], sortIndex };
    }
    groupMap[key].specials.push(special);
  });
  const groups = Object.values(groupMap).sort((a, b) => {
    const orderA = LABEL_ORDER[a.label] ?? a.sortIndex;
    const orderB = LABEL_ORDER[b.label] ?? b.sortIndex;
    return orderA - orderB;
  });
  return (
    <div className="vep-section">
      {groups.map((group, gi) => (
        <div key={gi} className="vep-specials-group">
          <div className="vep-specials-header">
            <img src="/images/date_icon.png" alt="" className="date-icon" onError={e => e.target.style.display='none'} />
            {group.label}
          </div>
          <div className="vep-specials-list">
            {group.specials.map((special, si) => {
              const desc = special.Description || '';
              const showTitle = special['Hide Title'] !== true;
              const specialName = special['Special Name'] || '';
              const parsedItems = parseItemsFromDescription(desc);
              if (parsedItems.length >= 1) {
                return (
                  <div key={si} className="deal deal-with-items">
                    {showTitle && specialName && <strong className="deal-name">{specialName}</strong>}
                    {parsedItems.map((item, ii) => (
                      <ContentItem key={ii} name={item.item} price={item.price} description={item.description || null} noBorder={ii === parsedItems.length - 1} />
                    ))}
                  </div>
                );
              }
              return (
                <ContentItem
                  key={si}
                  name={showTitle ? specialName : (extractPrice(desc) ? '' : specialName)}
                  price={extractPrice(desc)}
                  description={desc}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CleanseModal ──────────────────────────────────────────────────────────────

const CONTENT_LABELS = { deal: 'Happy Hour Deal', event: 'Event', special: 'Daily Special' };

// Maps form-key fix names → Airtable field names
const FIX_FIELD_MAP = {
  description:  'Description',
  startTime:    'Start Time',
  endTime:      'End Time',
  eventName:    'Event Name',
};

function applyFixesInMemory(content, issues) {
  if (!content) return null;
  const fixable = issues.filter(i => i.fixable);

  const patch = (records, type) => records.map(record => {
    const recordFixes = fixable
      .filter(i => i.contentType === type && i.recordId === record.id)
      .reduce((acc, i) => ({ ...acc, ...i.fix }), {});
    if (!Object.keys(recordFixes).length) return record;
    const patched = { ...record };
    Object.entries(recordFixes).forEach(([key, val]) => {
      patched[FIX_FIELD_MAP[key] || key] = val;
    });
    return patched;
  });

  return {
    deals:    patch(content.deals    || [], 'deal'),
    events:   patch(content.events   || [], 'event'),
    specials: patch(content.specials || [], 'special'),
  };
}

function CleansePreviewPane({ content }) {
  if (!content) return null;
  const hasDeals    = content.deals?.length > 0;
  const hasEvents   = content.events?.length > 0;
  const hasSpecials = content.specials?.length > 0;
  return (
    <div className="ve-cleanse-preview-pane">
      {hasDeals && (
        <div className="ve-cleanse-preview-section">
          <div className="ve-cleanse-preview-section-title">🍺 Happy Hours</div>
          <DealsPreview deals={content.deals} />
        </div>
      )}
      {hasEvents && (
        <div className="ve-cleanse-preview-section">
          <div className="ve-cleanse-preview-section-title">📅 Events</div>
          <EventsPreview events={content.events} />
        </div>
      )}
      {hasSpecials && (
        <div className="ve-cleanse-preview-section">
          <div className="ve-cleanse-preview-section-title">🍽 Daily Specials</div>
          <SpecialsPreview specials={content.specials} />
        </div>
      )}
    </div>
  );
}

function CleanseModal({ modal, venue, content, onClose, onFix, onFixAll, onAiFix, onAiApply, onAiDiscard, onAiFixAll, onEdit }) {
  const { issues, fixing, fixed, aiFixing, aiProposed, aiError } = modal;
  const [view, setView] = useState('issues'); // 'issues' | 'before' | 'after'

  const fixableCount   = issues.filter(i => i.fixable && !fixed.has(i.id)).length;
  const aiFixableCount = issues.filter(i => i.aiFixable && !fixed.has(i.id) && !aiProposed[i.id] && !aiFixing.has(i.id)).length;
  const aiPendingCount = Object.keys(aiProposed).filter(id => !fixed.has(id)).length;
  const totalIssues    = issues.filter(i => !fixed.has(i.id)).length;
  const anyAiInFlight  = aiFixing.size > 0;

  const patchedContent = useMemo(
    () => applyFixesInMemory(content, issues.filter(i => i.fixable)),
    [content, issues]
  );

  const groups = {};
  issues.forEach(issue => {
    if (!groups[issue.contentType]) groups[issue.contentType] = [];
    groups[issue.contentType].push(issue);
  });

  return (
    <div className="ve-modal-backdrop" onClick={onClose}>
      <div className="ve-cleanse-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ve-preview-header">
          <div>
            <div className="ve-preview-label">Cleanse Data · {venue?.name}</div>
            <div className="ve-preview-title">
              {totalIssues === 0
                ? '✅ All content looks good!'
                : `${totalIssues} issue${totalIssues !== 1 ? 's' : ''} found`}
            </div>
          </div>
          <button className="ve-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* View tabs */}
        <div className="ve-cleanse-view-tabs">
          <button
            className={`ve-cleanse-view-tab ${view === 'issues' ? 'active' : ''}`}
            onClick={() => setView('issues')}
          >
            ⚠️ Issues{totalIssues > 0 ? ` (${totalIssues})` : ''}
          </button>
          <button
            className={`ve-cleanse-view-tab ${view === 'before' ? 'active' : ''}`}
            onClick={() => setView('before')}
          >
            Current
          </button>
          <button
            className={`ve-cleanse-view-tab ${view === 'after' ? 'active' : ''}`}
            onClick={() => setView('after')}
          >
            {fixableCount > 0 ? `✨ After Fixes (${fixableCount})` : 'After Fixes'}
          </button>
        </div>

        {/* WIP notice */}
        <div className="ve-cleanse-wip-banner">
          <span className="ve-wip-badge">WIP</span> AI fixes and auto-cleanse are experimental — review all changes carefully before applying.
        </div>

        {/* Fix-all bar — only on issues tab */}
        {view === 'issues' && (fixableCount > 0 || aiFixableCount > 0 || aiPendingCount > 0) && (
          <div className="ve-cleanse-fixall">
            {fixableCount > 0 && <>
              <span className="ve-cleanse-fixall-note">{fixableCount} auto-fixable</span>
              <button className="ve-cleanse-fixall-btn" onClick={onFixAll}>Fix All</button>
            </>}
            {aiFixableCount > 0 && (
              <button
                className={`ve-cleanse-ai-fixall-btn ${anyAiInFlight ? 'loading' : ''}`}
                disabled={anyAiInFlight}
                onClick={onAiFixAll}
              >
                {anyAiInFlight ? '✦ Getting AI fixes…' : `✦ AI Fix ${aiFixableCount}`}
              </button>
            )}
            {aiPendingCount > 0 && !anyAiInFlight && (
              <span className="ve-cleanse-ai-pending-note">{aiPendingCount} AI fix{aiPendingCount !== 1 ? 'es' : ''} ready to review</span>
            )}
          </div>
        )}

        {/* Before/After comparison hint bar */}
        {(view === 'before' || view === 'after') && fixableCount > 0 && (
          <div className="ve-cleanse-compare-bar">
            <span>
              {view === 'before'
                ? 'Showing current content — switch to "After Fixes" to preview changes'
                : `Showing content with ${fixableCount} auto-fix${fixableCount !== 1 ? 'es' : ''} applied — not saved yet`}
            </span>
            <button
              className="ve-cleanse-compare-switch"
              onClick={() => setView(view === 'before' ? 'after' : 'before')}
            >
              {view === 'before' ? 'See After →' : '← See Current'}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="ve-preview-body">

          {/* ── Issues tab ── */}
          {view === 'issues' && <>
            {totalIssues === 0 && (
              <div className="ve-cleanse-empty">
                <div className="ve-cleanse-empty-icon">✅</div>
                <p>All happy hours, events, and daily specials look great. No display issues detected.</p>
              </div>
            )}
            {['deal', 'event', 'special'].map(type => {
              const typeIssues = (groups[type] || []);
              if (!typeIssues.length) return null;
              const visible = typeIssues.filter(i => !fixed.has(i.id));
              if (!visible.length) return null;

              const byRecord = {};
              visible.forEach(i => {
                if (!byRecord[i.recordId]) byRecord[i.recordId] = { label: i.label, recordId: i.recordId, issues: [] };
                byRecord[i.recordId].issues.push(i);
              });

              return (
                <div key={type} className="ve-cleanse-section">
                  <div className="ve-cleanse-section-title">{CONTENT_LABELS[type]}s</div>
                  {Object.values(byRecord).map((rec, ri) => (
                    <div key={ri} className="ve-cleanse-record">
                      <div className="ve-cleanse-record-header">
                        <span className="ve-cleanse-record-label">{rec.label}</span>
                        <button className="ve-cleanse-edit-link" onClick={() => onEdit(type, rec.recordId)}>Edit →</button>
                      </div>
                      {rec.issues.map(issue => {
                        const proposed = aiProposed[issue.id];
                        const isAiFixing = aiFixing.has(issue.id);
                        const thisAiError = aiError[issue.id];
                        // Look up current value for diff display
                        const recordData = (content?.[`${type}s`] || []).find(r => r.id === issue.recordId);
                        const currentDesc = recordData?.Description || '';
                        const currentName = type === 'deal' ? (recordData?.['Deal Name'] || '') : (recordData?.['Special Name'] || '');
                        const proposedValue = proposed?.description ?? proposed?.name ?? '';
                        const beforeValue = proposed?.description !== undefined ? currentDesc : currentName;
                        return (
                          <div key={issue.id} className={`ve-cleanse-issue ve-cleanse-${issue.severity}${fixed.has(issue.id) ? ' ve-cleanse-fixed' : ''}`}>
                            <div className="ve-cleanse-issue-row">
                              <span className="ve-cleanse-issue-icon">{issue.fixable ? '🔧' : issue.aiFixable ? '✦' : '⚠️'}</span>
                              <span className="ve-cleanse-issue-msg">
                                {issue.msg}
                                {issue.proposed && !proposed && (
                                  <span className="ve-cleanse-proposed"> → "{issue.proposed}"</span>
                                )}
                              </span>
                              <div className="ve-cleanse-issue-actions">
                                {issue.fixable ? (
                                  <button className="ve-cleanse-fix-btn" onClick={() => onFix(issue)} disabled={fixing.has(issue.id)}>
                                    {fixing.has(issue.id) ? '…' : 'Auto-fix'}
                                  </button>
                                ) : null}
                                {issue.aiFixable && !proposed && (
                                  <button
                                    className={`ve-cleanse-ai-btn ${isAiFixing ? 'loading' : ''}`}
                                    disabled={isAiFixing}
                                    onClick={() => onAiFix(issue)}
                                  >
                                    {isAiFixing ? '✦ …' : '✦ AI Fix'}
                                  </button>
                                )}
                                {issue.editable && !proposed && (
                                  <button className="ve-cleanse-edit-btn" onClick={() => onEdit(type, rec.recordId)}>Edit</button>
                                )}
                              </div>
                            </div>
                            {thisAiError && <div className="ve-cleanse-ai-error">{thisAiError}</div>}
                            {proposed && (
                              <div className="ve-cleanse-ai-proposed">
                                <div className="ve-cleanse-ai-diff">
                                  <div className="ve-cleanse-ai-diff-side before">
                                    <span className="ve-cleanse-ai-diff-label">Before</span>
                                    <span className="ve-cleanse-ai-diff-text">{beforeValue || <em>empty</em>}</span>
                                  </div>
                                  <div className="ve-cleanse-ai-diff-arrow">→</div>
                                  <div className="ve-cleanse-ai-diff-side after">
                                    <span className="ve-cleanse-ai-diff-label">After</span>
                                    <span className="ve-cleanse-ai-diff-text">{proposedValue}</span>
                                  </div>
                                </div>
                                <div className="ve-cleanse-ai-diff-actions">
                                  <button className="ve-cleanse-ai-apply-btn" onClick={() => onAiApply(issue)}>Apply</button>
                                  <button className="ve-cleanse-ai-discard-btn" onClick={() => onAiDiscard(issue.id)}>Discard</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
          </>}

          {/* ── Before tab ── */}
          {view === 'before' && (
            <CleansePreviewPane content={content} />
          )}

          {/* ── After tab ── */}
          {view === 'after' && (
            <CleansePreviewPane content={patchedContent} />
          )}

        </div>
      </div>
    </div>
  );
}
